/**
 * Composer-style "symphony" model — the IF/ELSE rebalancing tree used
 * by the WHB Algo builder (Sergey 2026-05-21).
 *
 * The schema is taken from a real exported symphony JSON. A symphony is
 * a recursive tree of nodes; every node carries a `step` discriminator
 * and an optional `children` array:
 *
 *   root               — the symphony itself (name, rebalance cadence)
 *   wt-cash-equal       — weight children equally
 *   wt-cash-specified   — weight children by each child's `weight`
 *   wt-inverse-vol      — weight children by inverse volatility
 *   group               — a named grouping
 *   if                  — an IF/ELSE block; children are `if-child`s
 *   if-child            — one branch of an IF block: a condition, or
 *                         the else branch (`is-else-condition?`)
 *   asset               — a leaf holding (ticker)
 */

export type SymphonyStep =
  | "root"
  | "wt-cash-equal"
  | "wt-cash-specified"
  | "wt-inverse-vol"
  | "group"
  | "if"
  | "if-child"
  | "asset";

/** A weight as a fraction (`num/den`). `num` is sometimes a string. */
export interface SymWeight {
  num: number | string;
  den: number | string;
}

export interface SymphonyNode {
  id: string;
  step: SymphonyStep;
  // root
  name?: string;
  description?: string;
  rebalance?: string;
  assetClass?: string;
  // asset
  ticker?: string;
  exchange?: string;
  // weighting / grouping
  weight?: SymWeight;
  "window-days"?: number | string;
  // if-child condition
  comparator?: string;
  "is-else-condition?"?: boolean;
  "lhs-fn"?: string;
  "lhs-fn-params"?: Record<string, unknown>;
  "lhs-val"?: string;
  "lhs-window-days"?: number | string;
  "rhs-fixed-value?"?: boolean;
  "rhs-fn"?: string;
  "rhs-fn-params"?: Record<string, unknown>;
  "rhs-val"?: string;
  "rhs-window-days"?: number | string;
  "collapsed?"?: boolean;
  children?: SymphonyNode[];
}

// ---- Parsing --------------------------------------------------------------

export interface ParseResult {
  ok: boolean;
  root?: SymphonyNode;
  error?: string;
}

/** Parse + lightly validate a pasted/uploaded symphony JSON string. */
export function parseSymphony(text: string): ParseResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: "That doesn't look like valid JSON." };
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { ok: false, error: "Expected a JSON object at the top level." };
  }
  const root = data as SymphonyNode;
  if (root.step !== "root") {
    return {
      ok: false,
      error:
        'This isn\'t a symphony export — the top-level "step" should be "root".'
    };
  }
  if (!Array.isArray(root.children)) {
    return { ok: false, error: "The symphony has no children to display." };
  }
  return { ok: true, root };
}

/** Serialize a symphony tree back to pretty-printed JSON. */
export function exportSymphony(root: SymphonyNode): string {
  return JSON.stringify(root, null, 2);
}

// ---- Labels ---------------------------------------------------------------

/** Indicator-function display names. */
const FN_LABELS: Record<string, string> = {
  "relative-strength-index": "RSI",
  "current-price": "Current Price",
  "moving-average-price": "Moving Avg Price",
  "exponential-moving-average-price": "EMA Price",
  "cumulative-return": "Cumulative Return",
  "moving-average-return": "Moving Avg Return",
  "max-drawdown": "Max Drawdown",
  "standard-deviation-price": "Std Dev Price",
  "standard-deviation-return": "Std Dev Return"
};

const COMPARATOR_LABELS: Record<string, string> = {
  gt: ">",
  lt: "<",
  gte: "≥",
  lte: "≤",
  eq: "=",
  "greater-than": ">",
  "less-than": "<"
};

export function fnLabel(fn?: string): string {
  if (!fn) return "";
  return FN_LABELS[fn] ?? fn.replace(/-/g, " ");
}

/** Human-readable label for one side of a condition. */
function sideLabel(
  fn: string | undefined,
  params: Record<string, unknown> | undefined,
  val: string | undefined
): string {
  if (!fn) return val ?? "—";
  const window = params?.["window"];
  const w = window != null ? ` ${window}d` : "";
  return `${fnLabel(fn)}${w} · ${val ?? "—"}`;
}

/** Plain-language text for an `if-child` condition (or "ELSE"). */
export function conditionText(node: SymphonyNode): string {
  if (node["is-else-condition?"]) return "ELSE";
  const lhs = sideLabel(node["lhs-fn"], node["lhs-fn-params"], node["lhs-val"]);
  const cmp =
    COMPARATOR_LABELS[node.comparator ?? ""] ?? node.comparator ?? "?";
  const rhs = node["rhs-fixed-value?"]
    ? String(node["rhs-val"] ?? "")
    : sideLabel(node["rhs-fn"], node["rhs-fn-params"], node["rhs-val"]);
  return `${lhs} ${cmp} ${rhs}`;
}

/** Short kind label for a node-type badge. */
export function stepKind(step: SymphonyStep): string {
  switch (step) {
    case "root":
      return "Symphony";
    case "wt-cash-equal":
    case "wt-cash-specified":
    case "wt-inverse-vol":
      return "Weight";
    case "group":
      return "Group";
    case "if":
      return "IF / ELSE";
    case "if-child":
      return "Branch";
    case "asset":
      return "Asset";
  }
}

/** Primary title for a node row / page header. */
export function nodeTitle(n: SymphonyNode): string {
  switch (n.step) {
    case "root":
      return n.name || "Symphony";
    case "group":
      return n.name || "Group";
    case "asset":
      return n.ticker || "Asset";
    case "if":
      return "IF / ELSE";
    case "if-child":
      return n["is-else-condition?"] ? "ELSE" : conditionText(n);
    case "wt-cash-equal":
      return "Equal weight";
    case "wt-cash-specified":
      return "Specified weight";
    case "wt-inverse-vol":
      return "Inverse-volatility weight";
    default:
      return n.step;
  }
}

/** Optional secondary line for a node row. */
export function nodeSubtitle(n: SymphonyNode): string {
  switch (n.step) {
    case "root":
      return `${n.rebalance ? `Rebalances ${n.rebalance}` : ""}${
        n.assetClass ? ` · ${n.assetClass.toLowerCase()}` : ""
      }`.trim();
    case "asset":
      return n.name ?? "";
    case "wt-inverse-vol":
      return n["window-days"] ? `${n["window-days"]}-day window` : "";
    default:
      return "";
  }
}

// ---- Weight ---------------------------------------------------------------

/** Resolve a `{num,den}` weight to a percentage, or null if absent. */
export function weightPct(w?: SymWeight): number | null {
  if (!w) return null;
  const num = typeof w.num === "string" ? parseFloat(w.num) : w.num;
  const den = typeof w.den === "string" ? parseFloat(w.den) : w.den;
  if (!Number.isFinite(num) || !den) return null;
  return (num / den) * 100;
}

/** Formatted weight badge text (e.g. "60%") or null. */
export function weightLabel(w?: SymWeight): string | null {
  const pct = weightPct(w);
  if (pct == null) return null;
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

// ---- Tree helpers ---------------------------------------------------------

/** Direct children (never undefined). */
export function childrenOf(n: SymphonyNode): SymphonyNode[] {
  return Array.isArray(n.children) ? n.children : [];
}

/** A node is drillable when it has children to navigate into. */
export function isDrillable(n: SymphonyNode): boolean {
  return childrenOf(n).length > 0;
}

/** Count every descendant node (for "N nodes" summaries). */
export function countDescendants(n: SymphonyNode): number {
  return childrenOf(n).reduce((sum, c) => sum + 1 + countDescendants(c), 0);
}

/** Count leaf `asset` nodes anywhere under `n`. */
export function countAssets(n: SymphonyNode): number {
  if (n.step === "asset") return 1;
  return childrenOf(n).reduce((sum, c) => sum + countAssets(c), 0);
}

/**
 * Resolve a path of node ids (from root) to the node it points at, plus
 * the chain of nodes along the way (for breadcrumbs). Unknown ids stop
 * the walk early.
 */
export function resolvePath(
  root: SymphonyNode,
  pathIds: string[]
): SymphonyNode[] {
  const chain: SymphonyNode[] = [root];
  let cur = root;
  for (const id of pathIds) {
    const next = childrenOf(cur).find((c) => c.id === id);
    if (!next) break;
    chain.push(next);
    cur = next;
  }
  return chain;
}
